import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { VariantService } from './variant.service';
import { InsuranceVariantService } from './insurance-variant.service';
import { getStateCode } from '../data/state-visit-config';

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'gclid', 'gad_source', 'gad_campaignid'];

export interface AppointmentsDataInitialParams {
  firstName: string;
  lastName: string;
  dob: string;
  birthsex: string;
  phone: string;
  email: string;
  state: string;
  partnerName?: string;
  partnerType?: string;
}

export interface AppointmentsDataInsuranceParams {
  primaryCarrier?: string;
  subscriberNumber?: string;
  groupNumber?: string;
  secondaryCarrier?: string;
  secondarySubscriberNumber?: string;
  secondaryGroupNumber?: string;
}

export interface AppointmentsDataScreeningParams {
  cognitive_diagnosis?: string;
  medication_independence?: string;
  clinical_decision?: string;
  technology_independence?: string;
}

export interface AppointmentsDataScheduledParams {
  patientId?: string;
  dateReceived?: string;
  timeReceived?: string;
  firstName?: string;
  lastName?: string;
  dob?: string;
  birthsex?: string;
  phone?: string;
  email?: string;
  appointmentDate?: string;
  preferredProvider?: string;
  state?: string;
  utm?: Record<string, string>;
  internalUser?: string;
  versionAssigned?: string;
  insurance?: AppointmentsDataInsuranceParams;
  olderAdultsScreening?: AppointmentsDataScreeningParams;
  heardAboutUs?: string;
  heardAboutUsOption?: string;
  partnerName?: string;
  partnerType?: string;
}

/**
 * Sends the same lead/appointment data already captured for the Google Sheet
 * (see SheetLoggerService) to the OLS_appointments_data Postgres-backed API.
 * Every call is fire-and-forget: callers must not await these methods before
 * proceeding with navigation or the primary scheduling flow.
 */
@Injectable({ providedIn: 'root' })
export class AppointmentsDataService {
  private static readonly ID_KEY = 'apptdata.id';
  // The AMD table has its own ids, so its row id is cached separately from the Postgres one.
  private static readonly AMD_ID_KEY = 'apptdata.amd.id';

  constructor(
    private api: ApiService,
    private variantService: VariantService,
    private insuranceVariant: InsuranceVariantService
  ) {}

  private readUtmFromStorage(): Record<string, string> {
    const out: Record<string, string> = {};
    try {
      if (typeof localStorage === 'undefined') return out;
      for (const k of UTM_KEYS) {
        out[k] = localStorage.getItem(`utm.${k}`) || '';
      }
    } catch {}
    return out;
  }

  private readInternalUserValue(): string {
    try {
      const cookie = (typeof document !== 'undefined' && typeof document.cookie === 'string') ? document.cookie : '';
      if (cookie && /(?:^|;\s*)internal_user=true(?:;|$)/i.test(cookie)) return 'Yes';
    } catch {}
    try {
      const params = new URLSearchParams((typeof window !== 'undefined' && window.location.search) || '');
      if ((params.get('internal_user') || '').toString().trim().toLowerCase() === 'true') return 'Yes';
    } catch {}
    try {
      const href = (typeof window !== 'undefined' && window.location && window.location.href) ? String(window.location.href) : '';
      if (href.toLowerCase().includes('internal_user=true')) return 'Yes';
    } catch {}
    return 'No';
  }

  /** The insurance flow assigned to the current session_id. */
  private readVersionAssigned(): string {
    try {
      const assignment = this.insuranceVariant.getCachedAssignment();
      const name = (assignment?.version_name || '').toString().trim();
      if (name) return name;
      return (assignment?.version_code || '').toString().trim();
    } catch {
      return '';
    }
  }

  private getCachedId(): string {
    try {
      return (typeof localStorage !== 'undefined' && localStorage.getItem(AppointmentsDataService.ID_KEY)) || '';
    } catch {
      return '';
    }
  }

  private setCachedId(id: string): void {
    try {
      if (id) localStorage.setItem(AppointmentsDataService.ID_KEY, id);
    } catch {}
  }

  private clearCachedId(): void {
    try {
      localStorage.removeItem(AppointmentsDataService.ID_KEY);
    } catch {}
  }

  private getCachedAmdId(): string {
    try {
      return (typeof localStorage !== 'undefined' && localStorage.getItem(AppointmentsDataService.AMD_ID_KEY)) || '';
    } catch {
      return '';
    }
  }

  private setCachedAmdId(id: string): void {
    try {
      if (id) localStorage.setItem(AppointmentsDataService.AMD_ID_KEY, id);
    } catch {}
  }

  private clearCachedAmdId(): void {
    try {
      localStorage.removeItem(AppointmentsDataService.AMD_ID_KEY);
    } catch {}
  }

  /** Matches SheetLoggerService.nowDate() exactly so Date_received is byte-identical across both calls. */
  private nowDate(): string {
    const d = new Date();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
  }

  /** Matches SheetLoggerService.nowTime() exactly so Time_received is byte-identical across both calls. */
  private nowTime(): string {
    const d = new Date();
    const hh = String(d.getHours()).padStart(2, '0');
    const mi = String(d.getMinutes()).padStart(2, '0');
    const ss = String(d.getSeconds()).padStart(2, '0');
    return `${hh}:${mi}:${ss}`;
  }

  /** Reads the shared sheet.Date_received/Time_received cache, seeding it if this is the first call of the session. */
  private resolveDateTimeReceived(): { dateReceived: string; timeReceived: string } {
    let dateReceived = (typeof localStorage !== 'undefined' && localStorage.getItem('sheet.Date_received')) || '';
    let timeReceived = (typeof localStorage !== 'undefined' && localStorage.getItem('sheet.Time_received')) || '';
    if (!dateReceived) {
      dateReceived = this.nowDate();
      try { localStorage.setItem('sheet.Date_received', dateReceived); } catch {}
    }
    if (!timeReceived) {
      timeReceived = this.nowTime();
      try { localStorage.setItem('sheet.Time_received', timeReceived); } catch {}
    }
    return { dateReceived, timeReceived };
  }

  /** Normalizes any of "NC" / "North Carolina" / "north carolina" to the 2-letter code. */
  private normalizeState(input?: string): string {
    try {
      return getStateCode(input || '') || (input || '').trim();
    } catch {
      return (input || '').trim();
    }
  }

  /** Normalizes any of "M" / "male" / "Male" / "prefer-not-to-say" etc. to M/F/U. */
  private normalizeBirthsex(input?: string): string {
    const v = (input || '').toString().trim().toLowerCase();
    if (!v) return '';
    if (v.startsWith('m')) return 'M';
    if (v.startsWith('f')) return 'F';
    return 'U';
  }

  private hasAnyInsuranceInfo(ins: AppointmentsDataInsuranceParams): boolean {
    return !!(
      ins.primaryCarrier ||
      ins.subscriberNumber ||
      ins.groupNumber ||
      ins.secondaryCarrier ||
      ins.secondarySubscriberNumber ||
      ins.secondaryGroupNumber
    );
  }

  private applyUtm(payload: any, utm: Record<string, string>): void {
    payload.utm_source = utm['utm_source'] || '';
    payload.utm_medium = utm['utm_medium'] || '';
    payload.utm_campaign = utm['utm_campaign'] || '';
    payload.utm_term = utm['utm_term'] || '';
    payload.utm_content = utm['utm_content'] || '';
    payload.Gclid = utm['gclid'] || '';
    payload.Google_Click_ID = utm['gclid'] || '';
    payload.gad_source = utm['gad_source'] || '';
    payload.Gad_campaignid = utm['gad_campaignid'] || '';
  }

  /** Fires on "See Available Slots". No-ops if already sent this session or if required data is missing. */
  sendInitial(params: AppointmentsDataInitialParams): void {
    const needPostgres = !this.getCachedId();
    const needAmd = !this.getCachedAmdId();
    if (!needPostgres && !needAmd) return;

    const email = (params.email || '').trim();
    const firstName = (params.firstName || '').trim();
    const lastName = (params.lastName || '').trim();
    const dob = (params.dob || '').trim();
    if (!email || !firstName || !lastName || !dob) return;

    const { dateReceived, timeReceived } = this.resolveDateTimeReceived();

    const payload: any = {
      Source: 'Online Intake',
      Version: 'B',
      session_uuid: this.variantService.getSessionUuid(),
      Date_received: dateReceived,
      Time_received: timeReceived,
      First_name: firstName,
      Last_name: lastName,
      DOB: dob,
      Birthsex: this.normalizeBirthsex(params.birthsex),
      Phone: (params.phone || '').trim(),
      Email: email,
      State: this.normalizeState(params.state),
      Internal_User: this.readInternalUserValue(),
    };
    const versionAssigned = this.readVersionAssigned();
    if (versionAssigned) payload.version_assigned = versionAssigned;
    if (params.partnerName) payload.Partner_name = params.partnerName;
    if (params.partnerType) payload.Partner_type = params.partnerType;
    this.applyUtm(payload, this.readUtmFromStorage());

    // Independent fire-and-forget calls: one failing never affects the other.
    if (needPostgres) {
      this.api.dispatch<any>('/appointments_data', payload).subscribe({
        next: (res) => { this.setCachedId(((res && res.id) ?? '').toString()); },
        error: () => {},
      });
    }
    if (needAmd) {
      this.api.dispatch<any>('/appointments_data_amd', payload).subscribe({
        next: (res) => { this.setCachedAmdId(((res && res.id) ?? '').toString()); },
        error: () => {},
      });
    }
  }

  /**
   * Payloads for the "scheduled" update, sent inside the /schedule request: the API updates the row created by
   * sendInitial (or inserts one if it never fired) in both tables, in the background. Scheduled and Patient_id
   * are filled in by the API. Postgres and AMD ids differ, so each table gets its own payload.
   */
  buildScheduledUpdate(params: AppointmentsDataScheduledParams): { postgres: any; amd: any } {
    return {
      postgres: this.buildScheduledPayload(params, this.getCachedId()),
      amd: this.buildScheduledPayload(params, this.getCachedAmdId()),
    };
  }

  /** Call once /schedule succeeded: the API has taken over the update, so the cached row ids are done with. */
  clearCachedIds(): void {
    this.clearCachedId();
    this.clearCachedAmdId();
  }

  /** `cachedId` is the row id of the table the payload is for (Postgres and AMD ids are different). */
  private buildScheduledPayload(params: AppointmentsDataScheduledParams, cachedId: string): any {
    const payload: any = {
      Version: 'B',
      session_uuid: this.variantService.getSessionUuid(),
      Scheduled: 'Yes',
    };
    if (params.patientId) payload.Patient_id = params.patientId.toString();
    if (cachedId) {
      const idNum = Number(cachedId);
      if (!Number.isNaN(idNum)) payload.id = idNum;
    }
    if (typeof payload.id === 'number') {
      // Updating a known row by id: only touch Date/Time_received if we actually have the
      // originally-captured values — never overwrite the lead's real receipt time with "now".
      if (params.dateReceived) payload.Date_received = params.dateReceived;
      if (params.timeReceived) payload.Time_received = params.timeReceived;
    } else {
      // No cached id (sendInitial never fired/succeeded): we need a natural key to upsert
      // against, generated with the same format sendInitial/SheetLoggerService use.
      const fallback = this.resolveDateTimeReceived();
      payload.Date_received = params.dateReceived || fallback.dateReceived;
      payload.Time_received = params.timeReceived || fallback.timeReceived;
    }
    if (params.firstName) payload.First_name = params.firstName;
    if (params.lastName) payload.Last_name = params.lastName;
    if (params.dob) payload.DOB = params.dob;
    if (params.birthsex) payload.Birthsex = this.normalizeBirthsex(params.birthsex);
    if (params.phone) payload.Phone = params.phone;
    if (params.email) payload.Email = params.email;
    if (params.appointmentDate) payload.Appointment_date = params.appointmentDate;
    if (params.preferredProvider) payload.Preferred_provider = params.preferredProvider;
    if (params.state) payload.State = this.normalizeState(params.state);
    if (params.internalUser) payload.Internal_User = params.internalUser;
    const versionAssigned = params.versionAssigned || this.readVersionAssigned();
    if (versionAssigned) payload.version_assigned = versionAssigned;
    if (params.heardAboutUs) payload.Heard_about_us = params.heardAboutUs;
    if (params.heardAboutUsOption) payload.Option = params.heardAboutUsOption;
    if (params.partnerName) payload.Partner_name = params.partnerName;
    if (params.partnerType) payload.Partner_type = params.partnerType;
    if (params.utm) this.applyUtm(payload, params.utm);

    if (params.insurance) {
      const ins = params.insurance;
      payload.Self_Pay_or_with_Insurance = this.hasAnyInsuranceInfo(ins) ? 'Insurance' : 'Selfpay';
      if (ins.primaryCarrier) payload.Primary_carrier = ins.primaryCarrier;
      if (ins.subscriberNumber) payload.Primary_subscriber_number = ins.subscriberNumber;
      if (ins.groupNumber) payload.Primary_group_number = ins.groupNumber;
      if (ins.secondaryCarrier) payload.Secondary_carrier = ins.secondaryCarrier;
      if (ins.secondarySubscriberNumber) payload.Secondary_subscriber_number = ins.secondarySubscriberNumber;
      if (ins.secondaryGroupNumber) payload.Secondary_group_number = ins.secondaryGroupNumber;
    }
    if (params.olderAdultsScreening) {
      const s = params.olderAdultsScreening;
      if (s.cognitive_diagnosis !== undefined) payload.cognitive_diagnosis = s.cognitive_diagnosis;
      if (s.medication_independence !== undefined) payload.medication_independence = s.medication_independence;
      if (s.clinical_decision !== undefined) payload.clinical_decision = s.clinical_decision;
      if (s.technology_independence !== undefined) payload.technology_independence = s.technology_independence;
    }

    return payload;
  }
}
